<template>
  <section
    v-if="displayedArticles.length"
    class="article-related-articles-module-component">
    <div class="rr-container">
      <div class="title-container">
        <h2
          v-if="data.title"
          v-html="data.title"></h2>
      </div>

      <div class="cards-container">
        <a
          v-for="(carousel, idx) of displayedArticles"
          :key="carousel?.id || `related-article-${idx}`"
          :href="carousel?.permalink || '#'"
          :alt="carousel?.post_title || ''"
          :target="carousel?.permalink ? '_self' : '_blank'"
          class="carousel-card">
          <div
            v-if="carousel?.acf?.carrousel_image?.url"
            class="card-image">
            <img
              :src="carousel.acf.carrousel_image.url"
              :alt="carousel.acf.carrousel_image.alt || ''"
              class="card-image-tag" />
          </div>

          <div class="card-text">
            <div class="title-info">
              <!-- <p
                v-if="articleCategoryName(carousel)"
                class="article-category"
                v-html="articleCategoryName(carousel)"></p> -->

              <p
                v-if="carousel?.post_title"
                class="article-name"
                v-html="carousel.post_title"></p>

              <div
                v-if="carousel?.acf?.description"
                class="description"
                v-html="carousel.acf.description"></div>
            </div>

            <a
              :href="carousel?.permalink || '#'"
              :aria-label="`Read ${plainText(carousel?.post_title)}`"
              class="carousel-link">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="53"
                height="58"
                viewBox="0 0 53 58"
                fill="none"
                aria-hidden="true">
                <path
                  d="M22.9732 0.914679C25.1184 -0.304941 27.7473 -0.304939 29.8925 0.914681L49.3253 11.9628C51.5138 13.2071 52.8656 15.5306 52.8656 18.0481V39.9588C52.8656 42.4763 51.5138 44.7999 49.3253 46.0441L29.8925 57.0923C27.7473 58.3119 25.1184 58.3119 22.9732 57.0923L3.54033 46.0441C1.35182 44.7999 0 42.4763 0 39.9588V18.0481C0 15.5306 1.35183 13.2071 3.54033 11.9628L22.9732 0.914679Z"
                  fill="#13D377" />
                <path
                  d="M33.4544 36.3491L33.0878 35.9825L33.0783 29.8648C33.0729 26.4138 33.3589 25.3946 33.3321 25.0024C33.3317 24.7409 33.2532 24.6624 33.1223 24.5837C32.9915 24.5051 32.8347 24.5572 32.6781 24.7138C32.3648 25.027 31.5822 26.1238 29.3895 28.3165L19.2875 38.4185L18.7646 38.4177L17.691 37.3441L17.6902 36.8213L27.8183 26.6931C30.011 24.5005 31.1079 23.7178 31.4211 23.4046C31.5777 23.248 31.6298 23.0912 31.5511 22.9604C31.4725 22.8295 31.3939 22.751 31.1325 22.7506C30.7665 22.75 29.7211 23.0098 26.2701 23.0044L20.1523 22.9949L19.7858 22.6283L19.7836 21.2165L20.149 20.851L34.7375 20.8738L35.2088 21.3451L35.2577 35.9598L34.8923 36.3252L33.4805 36.323L33.4544 36.3491Z"
                  fill="#0E4343" />
              </svg>
            </a>
          </div>
        </a>
      </div>
    </div>
  </section>
</template>

<script setup>
import useFooterBackgroundClass from '@/components/composables/useFooterBackgroundClass.js'
import { useStore } from '@/stores/main'
import { computed, ref, watch } from 'vue'

const humbleFooterClass = 'article-related-humble-footer'

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  post: {
    type: Object,
    required: true,
  },
})

const store = useStore()
const newestArticles = ref([])
let newestArticlesRequestId = 0

const enabled = (value) => value === true || value === 1 || value === '1'

useFooterBackgroundClass(
  humbleFooterClass,
  () => props.data?.update_footer_background
)

const pickArticles = computed(() => enabled(props.data?.pick_articles))

const normalizeArticle = (article) => ({
  ...article,
  post_title: article?.post_title || article?.title?.rendered || '',
  permalink: article?.permalink || article?.link || '',
})

const selectedArticles = computed(() =>
  Array.isArray(props.data?.articles)
    ? props.data.articles.slice(0, 3).map(normalizeArticle)
    : []
)

const displayedArticles = computed(() =>
  pickArticles.value ? selectedArticles.value : newestArticles.value
)

const loadNewestArticles = async () => {
  const activeRequest = ++newestArticlesRequestId

  if (pickArticles.value) {
    newestArticles.value = []
    return
  }

  try {
    const params = {
      per_page: 3,
      orderby: 'date',
      order: 'desc',
      ...(props.post?.id ? { exclude: props.post.id } : {}),
    }
    const request = {
      type: 'articles',
      params,
      showLoading: false,
    }

    await store.getItems(request)

    if (activeRequest !== newestArticlesRequestId) return

    newestArticles.value = store
      .requestedItems(request)
      .slice(0, 3)
      .map(normalizeArticle)
  } catch (error) {
    if (activeRequest !== newestArticlesRequestId) return

    newestArticles.value = []
    console.error('Unable to load related articles.', error)
  }
}

// const articleCategoryName = (article) => {
//   const category = article?.acf?.article_categories?.[0]

//   if (category && typeof category === 'object') {
//     return category.name || ''
//   }

//   return typeof category === 'string' && !/^\d+$/.test(category) ? category : ''
// }

const plainText = (value = '') => {
  const element = document.createElement('div')
  element.innerHTML = value || 'article'

  return element.textContent || element.innerText || 'article'
}

watch([pickArticles, () => props.post?.id], loadNewestArticles, {
  immediate: true,
})
</script>

<style lang="scss" scoped>
.article-related-articles-module-component {
  @apply bg-white relative py-10 lg:py-20;

  &:before {
    @apply absolute top-0 left-0 z-1 h-full w-full bg-balanced;
    content: '';
    @apply rounded-tl-[50px] md:rounded-tl-[60px] lg:rounded-tl-[80px];
  }

  .title-container {
    @apply flex flex-col gap-5 lg:gap-10 lg:pt-10;

    h2 {
      @apply header-2 text-white;
    }
  }

  .cards-container {
    @apply flex flex-col gap-6 pt-8 pb-[50px] lg:flex-row lg:gap-14;
  }

  .carousel-card {
    @apply relative flex w-full max-w-[500px] flex-1 flex-col items-center gap-6 overflow-hidden rounded-b-[27px] rounded-t-[14px] bg-white;
    @apply lg:max-w-[440px];

    &:before {
      @apply pointer-events-none absolute top-0 left-0 z-2 h-full w-full bg-balanced opacity-0 transition-opacity duration-300 ease-out;
      content: '';
    }

    .card-image {
      @apply h-[203px] w-full overflow-hidden rounded-[14px] object-cover object-center;
      @apply lg:h-[256px];

      img {
        @apply h-full w-full object-cover;
      }
    }

    .card-text {
      @apply flex w-full flex-1 flex-col px-8 pb-4 lg:gap-5 lg:px-6 lg:pb-6;

      .title-info {
        @apply mt-0;

        .description {
          :deep(*) {
            @apply mb-5 text-[16px] leading-[1.2] lg:text-[18px];
          }
        }

        .article-name {
          @apply header-5 mb-4 normal-case text-balanced;
        }

        .article-category {
          @apply mb-3 text-[13px] text-balanced;
        }
      }

      .carousel-link {
        @apply mt-auto self-end lg:translate-x-2;
      }
    }
  }
}
</style>

<style lang="scss">
body.article-related-humble-footer .site-footer {
  background: #1e6b5c !important;
}
</style>
